import React, {useEffect, useRef} from 'react'
import {useTranslation} from '@modifi/plugin-i18n'
import {ButtonPrimary, Caption, Col, H5, InfoAlert, Row} from '@modifi/ui'
import {OnboardingActivationStatus} from '@modifi/enums'

import {FormContentWrapper, StyledAddIcon, StyledSkeletonLine} from '../../styles'
import DynamicEntriesTable, {DynamicEntry} from '../../../../../component/dynamic-entries-table'
import CustomFormCtaFooter from '../../../../../plugins/onboarding/components/form-cta-footer/CustomFormCtaFooter'
import {SECTION_SELLER_TRADE_PARTNERS} from '../../../../../constants/sections'
import {BuyerSummaryEntry} from './selector'

const tableHeadlines = ['name', 'buyerLimit', 'status']

export type BuyerListProps = {
  buyers?: (BuyerSummaryEntry & DynamicEntry)[]
  redirectToCompanyFinancials: () => void
  showBuyerForm: (externalBuyerId?: string) => void
  onSubmit: () => void
  companyIsLocked?: boolean
  loadingLimits?: {
    buyerId: string
    timestamp: number
  }[]
  fetchBuyers: () => void
  removeLoadingLimit: (buyerId: string) => void
  activationStatus: string
}

const BuyerList: React.FC<BuyerListProps> = ({
  buyers = [],
  redirectToCompanyFinancials,
  showBuyerForm,
  onSubmit,
  companyIsLocked = false,
  loadingLimits = [],
  fetchBuyers,
  removeLoadingLimit,
  activationStatus,
}) => {
  const {t} = useTranslation(['charon', 'plugin-forms'])
  const intervalId = useRef<ReturnType<typeof setInterval> | null>(null)

  const handleEditBuyer = (buyer: DynamicEntry) => {
    showBuyerForm(buyer.externalBuyerId as string)
  }

  const handleSubmit = () => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'pre_onboarding_step_3_submit',
      element_id: 'pre_onboarding_step_3_submit',
      element_type: 'button',
    })
    onSubmit && onSubmit()
  }

  // fetching limits of buyer until 30 secs after submit
  useEffect(() => {
    if (loadingLimits && loadingLimits?.length) {
      intervalId.current = setInterval(() => {
        const hasBuyerLimitAlready =
          buyers?.find(buyer => buyer?.externalBuyerId === loadingLimits[0].buyerId)?.buyerLimit !==
          '-'

        if (new Date().getTime() - loadingLimits[0].timestamp > 30000 || hasBuyerLimitAlready) {
          removeLoadingLimit(loadingLimits[0].buyerId)
        } else {
          fetchBuyers()
        }
      }, 3000)
    }
    return () => {
      intervalId.current && clearInterval(intervalId.current)
    }
  }, [buyers, fetchBuyers, loadingLimits, removeLoadingLimit])

  const loadingLimitsBuyerIds = loadingLimits ? loadingLimits?.map(limit => limit.buyerId) : []

  const updatedBuyerSummaryData = buyers?.map(buyer => {
    if (loadingLimitsBuyerIds.includes(buyer?.externalBuyerId as string)) {
      return {...buyer, buyerLimit: <StyledSkeletonLine />}
    }
    return buyer
  })

  return (
    <>
      <Row justifyContent="between" alignItems="center" mb={16}>
        <Col col="auto">
          <H5>{t('intro-page.buyer-details.add-buyer.headline')}</H5>
        </Col>
        <Col col="auto">
          <ButtonPrimary
            data-target="add-buyer"
            onClick={() => showBuyerForm()}
            icon
            disabled={!!companyIsLocked}
          >
            <StyledAddIcon />
            {t(`sub-section-menu.${SECTION_SELLER_TRADE_PARTNERS}.create`)}
          </ButtonPrimary>
        </Col>
      </Row>
      <Row mb={24}>
        <Col md={9}>
          <Caption>{t('intro-page.buyer-details.add-buyer.description')}</Caption>
        </Col>
      </Row>
      <InfoAlert mb={24}>{t('seller-trade-partner.buyer-limit-calculation.info.label')}</InfoAlert>
      <FormContentWrapper>
        <Row mb={40}>
          <Col>
            <DynamicEntriesTable
              dataList={updatedBuyerSummaryData || []}
              headlines={tableHeadlines}
              transPrefix="buyer-detils-list"
              onItemClick={handleEditBuyer}
            />
          </Col>
        </Row>
      </FormContentWrapper>
      <CustomFormCtaFooter
        hasError={false}
        hasUnsavedChanges={false}
        // read-only list - there is nothing unsaved to prompt about on navigation
        hasNavLock={false}
        hasBackButton
        canMoveBack
        onBackButtonClick={redirectToCompanyFinancials}
        isAlwaysVisible
        hasDiscard={false}
        hasSubmit={activationStatus !== OnboardingActivationStatus.INCOMPLETE}
        onSubmit={() => null}
        hasForwardButton
        canMoveForward
        onForwardButtonClick={handleSubmit}
        submitLabel={t('sub-section-footer.cta-final-submit')}
        canSubmit={!companyIsLocked}
        disableSubmit={!!companyIsLocked}
        dataTargetSuffix="from-step-3"
        isFinalStep
      />
    </>
  )
}

export default BuyerList
